import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

const FormWrapper = styled.form`
  width: 100%;
  max-width: 1170px;
  margin: 0 auto;
  padding: 40px 20px 25px 20px;
  border-radius: 5px;
  box-shadow: 0px 0px 4px 0px #84064FFF;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.content};
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 8px;
`;

const sharedInputStyles = `
  background: ${({ theme }) => theme.colors.white};
  border: none;
  border-bottom: 2px solid ${({ theme }) => theme.colors.magenta};
  padding: 10px;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.content};
  font-size: 16px;
  outline: none;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.white};
    opacity: 0.7;
  }
`;

const Input = styled.input`
  ${sharedInputStyles}
`;

const TextArea = styled.textarea`
  ${sharedInputStyles}
  resize: vertical;
  min-height: 120px;
`;

const ConsentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 15px;
`;

const ConsentCheckbox = styled.input``;

const ConsentText = styled.p`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
`;

const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.colors.magenta};
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
  padding: 10px 15px;
  margin-top: 30px;
  border: none;
  border-radius: 5px;
  font-size: 18px;
  font-family: ${({ theme }) => theme.fonts.content};
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.25s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.p`
  color: ${({ theme }) => theme.colors.orange};
  text-align: center;
  margin-top: 20px;
`;

const ContactForm = () => {
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!recaptchaRef.current?.getValue()) {
      setError('Please verify you are not a robot.');
      return;
    }

    setLoading(true);

    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY);
      setSuccess(true);
      formRef.current.reset();
      recaptchaRef.current.reset();
    } catch (err) {
      console.error(err);
      setError('Something went wrong while sending your message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormWrapper ref={formRef} onSubmit={handleSubmit}>
        {/* First Name */}
        <Field>
          <Label htmlFor="firstName">First Name *</Label>
          <Input type="text" id="firstName" name="first_name" placeholder="First Name" required />
        </Field>

        {/* Last Name */}
        <Field>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input type="text" id="lastName" name="last_name" placeholder="Last Name" required />
        </Field>

        {/* Email */}
        <Field>
          <Label htmlFor="email">Email *</Label>
          <Input type="email" id="email" name="email" placeholder="Email" required />
        </Field>

        {/* Phone */}
        <Field>
          <Label htmlFor="phone">Phone *</Label>
          <Input type="tel" id="phone" name="phone" placeholder="Phone" required />
        </Field>

        {/* Website */}
        <Field>
          <Label htmlFor="website">Website (Optional)</Label>
          <Input type="text" id="website" name="website" placeholder="Web URL goes here" />
        </Field>

        {/* Company Name */}
        <Field>
          <Label htmlFor="company">Company Name (Optional)</Label>
          <Input type="text" id="company" name="company" placeholder="Company Name" />
        </Field>

        {/* Message */}
        <Field>
          <Label htmlFor="message">Message *</Label>
          <TextArea id="message" name="message" placeholder="Message" required />
        </Field>

        {/* Recaptcha */}
        <Field>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_RECAPTCHA_SITE_KEY'}
            theme="dark"
          />
        </Field>

        {/* Consent Checkbox */}
        <ConsentWrapper>
          <ConsentCheckbox type="checkbox" id="consent" name="consent" required />
          <ConsentText>
            I am granting Express Written Consent for IntelliCubix and its affiliates to communicate with me via SMS,
            phone call, or email. Standard message and data rates may apply.
          </ConsentText>
        </ConsentWrapper>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? 'Sending...' : 'Submit'}
        </SubmitButton>

        {success && <SuccessMessage>Your message has been sent successfully!</SuccessMessage>}
        {error && <SuccessMessage>{error}</SuccessMessage>}
      </FormWrapper>
    </>
  );
};

export default ContactForm;
